import * as React from "react";
import { useContext, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import ky from "ky";
import { datadogLogs } from "@datadog/browser-logs";

import { Paragraph } from "../../components/ui/typography";
import { InputHook } from "../../components/system/input-hook";
import { CheckboxWithLabel } from "../../components/system/checkbox-with-label";
import { Button } from "../../components/ui/button";
import { LoadingIcon } from "../../components/system/loading-icon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

import {
  apiErrorToMessage,
  getStandardHeaders,
  parseError,
} from "../../apiUtils";
import { UserContext } from "../../crud/user/context";
import { useQueryClient } from "react-query";

const updateProfileContactInfo = async (
  fullName: string,
  preferredName: string,
  title: string,
  company: string,
  workEmail: string,
  phoneNumber: string,
  canContact: boolean,
): Promise<{ error?: string }> => {
  /* eslint-disable @typescript-eslint/naming-convention */
  try {
    await ky.patch(`/api/v1/users/me/profile/`, {
      json: {
        full_name: fullName,
        preferred_name: preferredName,
        title,
        company,
        work_email: workEmail,
        phone_number: phoneNumber,
        can_contact: canContact,
      },
      headers: getStandardHeaders(),
    });
    return {};
    /* eslint-enable @typescript-eslint/naming-convention */
  } catch (err) {
    datadogLogs.logger.error("Error patching user profile");
    return { error: parseError(err) };
  }
};

interface UserInfoFormInput {
  fullName: string;
  preferredName: string;
  title: string;
  company: string;
  workEmail: string;
  phoneNumber: string;
  canContact: boolean;
}

interface UserInfoFormProps {
  open?: boolean;
  handleClose: () => void;
  formTitle: string;
  showCanContact?: boolean;
}

type SubmitState =
  | { type: "none" }
  | { type: "submitting" }
  | { type: "error"; error: string };

export const UserInfoForm = ({
  handleClose,
  open = true,
  formTitle,
  showCanContact = true,
}: UserInfoFormProps): React.ReactElement => {
  const { user } = useContext(UserContext);
  const methods = useForm<UserInfoFormInput>({
    mode: "onChange",
    defaultValues: {
      fullName: user.fullName,
      preferredName: user.preferredName,
      title: user.profile.title,
      company: user.profile.company,
      workEmail: user.profile.workEmail,
      phoneNumber: user.profile.phoneNumber,
      canContact: user.profile.canContact,
    },
  });
  const [submissionState, setSubmissionState] = useState<SubmitState>({
    type: "none",
  });
  const queryClient = useQueryClient();

  // Reset submission state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSubmissionState({ type: "none" });
    }
  }, [open]);

  const onSubmit = async (data: UserInfoFormInput) => {
    setSubmissionState({ type: "submitting" });
    const result = await updateProfileContactInfo(
      data.fullName,
      data.preferredName,
      data.title,
      data.company,
      data.workEmail,
      data.phoneNumber,
      data.canContact,
    );
    if (result.error) {
      setSubmissionState({
        type: "error",
        error: apiErrorToMessage(result.error),
      });
    } else {
      queryClient.invalidateQueries();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{formTitle}</DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <div className="flex flex-col gap-4">
              <Paragraph>All fields are optional.</Paragraph>
              <InputHook name="fullName" label="Full Name" />
              <InputHook name="preferredName" label="Preferred Name" />
              <InputHook name="title" label="Job Title" />
              <InputHook name="company" label="Company" />
              <InputHook
                name="workEmail"
                label={user.hasUsablePassword ? "Email" : "Work Email"}
                type="email"
                rules={{
                  required: user.hasUsablePassword
                    ? "Email is required"
                    : false,
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Please enter a valid email address",
                  },
                }}
              />
              <InputHook
                name="phoneNumber"
                label="Phone Number"
                rules={{
                  pattern: {
                    value: /^\+?(\s|-|[0-9\b]+)+$/,
                    message: "Can contain numbers and hyphens",
                  },
                }}
              />
              {showCanContact && (
                <CheckboxWithLabel
                  id="canContact"
                  label="Coiled can contact me"
                  checked={methods.watch("canContact")}
                  onCheckedChange={(checked) =>
                    methods.setValue("canContact", checked as boolean)
                  }
                />
              )}
            </div>
            <Paragraph className="mt-4">
              You can edit these any time from your profile page.
            </Paragraph>
            {submissionState.type === "error" && (
              <Paragraph className="text-destructive mt-2">
                {submissionState.error}
              </Paragraph>
            )}
            <div className="flex gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submissionState.type === "submitting"}
                className="flex-1"
              >
                {submissionState.type === "submitting" ? (
                  <LoadingIcon />
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
